/**
 * File utility functions
 * Functions for file operations and API interactions.
 * No dependencies on app state.
 */

const CHUNKED_THRESHOLD = 100 * 1024 * 1024; // 100MB — use multipart above this
const CHUNK_SIZE        =  10 * 1024 * 1024; // 10MB per chunk

/**
 * Convert a File object to base64 data URL.
 * Still used for avatar/logo crops that need canvas-encoded output.
 */
export function convertFileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
    });
}

function getAuthToken() {
    try {
        return window.authSystem?.getAuthToken?.() || localStorage.getItem('authToken') || '';
    } catch {
        return '';
    }
}

// Token placed in file URLs (img src, download links). Prefer the read-only file token:
// a URL ends up in browser history and logs, and the file token can only read files.
// Never fall back to a session token: URLs leak through browser history, referrers
// and logs. Login/verification always provisions this restricted token.
export function getFileLinkToken() {
    try {
        return localStorage.getItem('fileToken') || '';
    } catch {
        return '';
    }
}

function buildAuthHeaders(extraHeaders = {}) {
    const headers = { ...extraHeaders };
    const token = getAuthToken();
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    return headers;
}

/**
 * Build a raw binary URL for a file — works for both KV and R2 backends.
 * Suitable for img.src, fetch(), or any context that needs binary content.
 * @param {string} fileKey - The unique file key
 * @param {string|object} [filenameOrOptions] - Optional filename or options object
 */
export function buildFileReadUrl(fileKey, filenameOrOptions) {
    const options = typeof filenameOrOptions === 'string'
        ? { filename: filenameOrOptions }
        : (filenameOrOptions && typeof filenameOrOptions === 'object' ? filenameOrOptions : {});
    const includeAuth = options.includeAuth !== false && !options.publicToken;
    const token = includeAuth ? getFileLinkToken() : '';
    const authQuery = token ? `&authToken=${encodeURIComponent(token)}` : '';
    const publicTokenQuery = options.publicToken ? `&publicToken=${encodeURIComponent(options.publicToken)}` : '';
    const filenameQuery = options.filename ? `&filename=${encodeURIComponent(options.filename)}` : '';
    const downloadQuery = options.download ? '&download=1' : '';
    return `/api/files?key=${encodeURIComponent(fileKey)}&mode=raw${authQuery}${publicTokenQuery}${filenameQuery}${downloadQuery}`;
}

/**
 * Upload a File object to storage.
 *
 * Files ≤ 100MB  → single raw binary POST (fast, one request).
 * Files > 100MB  → R2 multipart upload in 10MB chunks (supports up to 350MB).
 *
 * @param {string}   fileKey    - Unique storage key
 * @param {File}     file       - File object to upload
 * @param {object}   [options]  - { kind, projectId, docId }
 * @param {Function} [onProgress] - Called with 0–100 during chunked uploads
 */
export async function uploadFile(fileKey, file, options = {}, onProgress = null) {
    if (file instanceof File || file instanceof Blob) {
        if (file.size > CHUNKED_THRESHOLD) {
            return _uploadChunked(fileKey, file, options, onProgress);
        }
        return _uploadSingle(fileKey, file, options, onProgress);
    }
    // Legacy: base64 data URL string — kept for avatar/logo crop path
    return _uploadLegacyBase64(fileKey, file, options);
}

async function _uploadSingle(fileKey, file, options, onProgress) {
    const params = new URLSearchParams({ key: fileKey });
    if (options.kind)          params.set('kind', options.kind);
    if (options.projectId != null) params.set('projectId', String(options.projectId));
    if (options.docId)         params.set('docId', String(options.docId));
    const uploadContentType = file.type || 'application/octet-stream';
    const fileName = String(options.fileName || file?.name || '').trim();
    if (onProgress) onProgress(0);

    const response = await fetch(`/api/files?${params}`, {
        method: 'POST',
        headers: buildAuthHeaders({
            'Content-Type': uploadContentType,
            'X-Upload-Content-Type': uploadContentType,
            ...(fileName ? { 'X-Upload-File-Name': fileName } : {})
        }),
        body: file
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Upload failed: ${response.status} ${response.statusText}`);
    }
    if (onProgress) onProgress(100);
}

async function _uploadChunked(fileKey, file, options, onProgress) {
    const contentType = file.type || 'application/octet-stream';
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const fileName = String(options.fileName || file?.name || '').trim();

    // 1. Init — get uploadId from R2
    const initRes = await fetch(`/api/files?key=${encodeURIComponent(fileKey)}&action=multipart-init`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ contentType })
    });
    if (!initRes.ok) {
        throw new Error(`Multipart init failed: ${initRes.status}`);
    }
    const { uploadId } = await initRes.json();

    // 2. Upload parts
    const parts = [];
    try {
        for (let i = 0; i < totalChunks; i++) {
            const start = i * CHUNK_SIZE;
            const end   = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);

            const partParams = new URLSearchParams({
                key: fileKey,
                action: 'multipart-part',
                uploadId,
                partNumber: String(i + 1)
            });
            const partRes = await fetch(`/api/files?${partParams}`, {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/octet-stream' }),
                body: chunk
            });
            if (!partRes.ok) {
                throw new Error(`Part ${i + 1}/${totalChunks} failed: ${partRes.status}`);
            }
            const { etag } = await partRes.json();
            parts.push({ partNumber: i + 1, etag });

            if (onProgress) {
                onProgress(Math.round((end / file.size) * 100));
            }
        }

        // 3. Complete — assemble in R2 and write D1 row
        const completeRes = await fetch(
            `/api/files?key=${encodeURIComponent(fileKey)}&action=multipart-complete`,
            {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    uploadId,
                    parts,
                    fileSize:    file.size,
                    contentType,
                    kind:        options.kind        || 'generic',
                    projectId:   options.projectId   ?? null,
                    docId:       options.docId        || null,
                    fileName
                })
            }
        );
        if (!completeRes.ok) {
            throw new Error(`Multipart complete failed: ${completeRes.status}`);
        }
    } catch (err) {
        // Abort the incomplete multipart upload to avoid orphaned R2 state
        await fetch(
            `/api/files?key=${encodeURIComponent(fileKey)}&action=multipart-abort&uploadId=${encodeURIComponent(uploadId)}`,
            { method: 'POST', headers: buildAuthHeaders() }
        ).catch(() => null);
        throw err;
    }
}

// Legacy path: base64 data URL string (avatar / logo crops via canvas)
async function _uploadLegacyBase64(fileKey, base64Data, options) {
    const useJsonPayload = options && typeof options === 'object' && Object.keys(options).length > 0;
    const response = await fetch(`/api/files?key=${encodeURIComponent(fileKey)}`, {
        method: 'POST',
        headers: buildAuthHeaders(
            useJsonPayload
                ? { 'Content-Type': 'application/json' }
                : { 'Content-Type': 'text/plain' }
        ),
        body: useJsonPayload
            ? JSON.stringify({
                data:        base64Data,
                projectId:   options.projectId   ?? null,
                docId:       options.docId        ?? null,
                kind:        options.kind         || 'generic',
                contentType: options.contentType  || 'text/plain',
                fileName:    options.fileName     || ''
              })
            : base64Data
    });

    if (!response.ok) {
        let errorMessage = `Failed to upload file: ${response.status} ${response.statusText}`;
        try {
            const errorData = await response.json();
            if (errorData.message) errorMessage = errorData.message;
        } catch (_) { /* ignore */ }
        throw new Error(errorMessage);
    }
}

/**
 * Delete a file from storage.
 */
export async function deleteFile(fileKey) {
    const response = await fetch(`/api/files?key=${encodeURIComponent(fileKey)}`, {
        method: 'DELETE',
        headers: buildAuthHeaders()
    });

    if (!response.ok) {
        throw new Error(`Failed to delete file: ${response.status} ${response.statusText}`);
    }
}
